<template>
  <div class="pb-10">
    <PageTitle title="Ventes" :description="`${total} vente(s) au total`" />

    <!-- Filtres -->
    <BaseCard class="mb-5">
      <div class="flex flex-wrap gap-4 items-end">
        <!-- Recherche -->
        <div class="flex-1 min-w-[250px]">
          <BaseInput v-model="search" type="search" placeholder="Rechercher une vente..."
            clearable @input="handleSearch">
            <template #icon-left>
              <i class="fa-solid fa-search"></i>
            </template>
          </BaseInput>
        </div>

        <!-- Filtres de date -->
        <div class="flex gap-3">
          <div class="flex flex-col gap-1">
            <label for="dateFrom" class="text-xs font-medium text-gray-500">Du</label>
            <BaseInput id="dateFrom" v-model="filters.dateFrom" type="date" @change="loadSales" />
          </div>
          <div class="flex flex-col gap-1">
            <label for="dateTo" class="text-xs font-medium text-gray-500">Au</label>
            <BaseInput id="dateTo" v-model="filters.dateTo" type="date" @change="loadSales" />
          </div>
        </div>

        <!-- Filtre de type -->
        <div class="flex flex-col gap-1">
          <label for="typeFilter" class="text-xs font-medium text-gray-500">Type</label>
          <select id="typeFilter" v-model="filters.type" @change="loadSales"
            class="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors min-w-[180px]">
            <option value="">Tous les types</option>
            <option value="single">Vente simple</option>
            <option value="deferred">Vente differee</option>
          </select>
        </div>
      </div>
    </BaseCard>

    <!-- Tableau des ventes -->
    <BaseCard padding="none">
      <!-- Chargement -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16">
        <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
        <p class="text-sm text-gray-400">Chargement des ventes...</p>
      </div>

      <!-- Etat vide -->
      <div v-else-if="sales.length === 0" class="flex flex-col items-center justify-center py-16 text-center px-6">
        <div class="text-gray-300 mb-4">
          <i class="fa-solid fa-file-invoice text-5xl"></i>
        </div>
        <h3 class="text-base font-semibold text-gray-700 mb-1">Aucune vente trouvee</h3>
        <p class="text-sm text-gray-400 max-w-sm mb-6">
          Cliquez sur "Nouvelle vente" pour commencer a enregistrer des ventes.
        </p>
        <router-link :to="{ name: 'sale-simple' }" v-permission="'make-sale'">
          <button class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors">
            <i class="fas fa-plus text-xs"></i>
            Commencer a vendre
          </button>
        </router-link>
      </div>

      <!-- Tableau -->
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="bg-gray-50/80 border-b border-gray-200">
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                N Facture
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Montant
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Paiement
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Vendeur
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th class="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="sale in sales" :key="sale.id" :class="[
              'hover:bg-gray-50/80 transition-colors',
              { 'opacity-50 bg-gray-50': sale.status === 'refunded' }
            ]">
              <td class="px-5 py-3.5">
                <span class="text-sm font-semibold text-gray-900 font-mono">{{ sale.invoice_number }}</span>
              </td>
              <td class="px-5 py-3.5 text-sm text-gray-500">
                {{ formatDate(sale.sale_date) }}
              </td>
              <td class="px-5 py-3.5">
                <span class="text-sm font-bold text-gray-900">{{ formatCurrency(sale.total_incl_tax) }}</span>
              </td>
              <td class="px-5 py-3.5">
                <span v-if="sale.customer && sale.customer.name" class="text-sm text-gray-900 font-medium">
                  {{ sale.customer.name }}
                </span>
                <span v-else class="text-sm text-gray-300 italic">-</span>
              </td>
              <td class="px-5 py-3.5 text-sm text-gray-500">
                {{ sale.payment_label || 'N/A' }}
              </td>
              <td class="px-5 py-3.5 text-sm text-gray-500">
                {{ sale.user ? sale.user.name : 'N/A' }}
              </td>
              <td class="px-5 py-3.5">
                <SaleStatusBadge :sale="sale" />
              </td>
              <td class="px-5 py-3.5">
                <div class="flex items-center justify-end gap-1">
                  <button
                    class="p-1.5 text-gray-400 hover:text-primary hover:bg-primary-50 rounded-lg transition-colors"
                    title="Telecharger PDF"
                    :disabled="sale.loading"
                    @click="downloadInvoice(sale)">
                    <i v-if="sale.loading" class="fas fa-spinner fa-spin text-sm"></i>
                    <i v-else class="fas fa-file-arrow-down text-sm"></i>
                  </button>
                  <router-link :to="{ name: 'sale-details', params: { id: sale.id } }">
                    <button class="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Voir details">
                      <i class="fas fa-eye text-sm"></i>
                    </button>
                  </router-link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="sales.length > 0" class="border-t border-gray-100 px-5 py-4">
        <PaginationComponent :current-page="currentPage" :per-page="perPage" :total="total" :to="paginationInfo.to"
          :show-page-numbers="true" :max-visible-pages="5" @page-change="goToPage" />
      </div>
    </BaseCard>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { salesService } from '@/services'
import PageTitle from '@/components/common/PageTitle.vue'
import BaseCard from '@/components/base/BaseCard.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import SaleStatusBadge from '@/components/sales/SaleStatusBadge.vue'

export default defineComponent({
  name: 'SalesView',
  components: {
    PageTitle,
    BaseCard,
    BaseInput,
    PaginationComponent,
    SaleStatusBadge
  },
  setup() {
    // Donnees d'etat
    const sales = ref([])
    const loading = ref(true)
    const isDownloading = ref(false)
    const search = ref('')
    const filters = reactive({
      dateFrom: '',
      dateTo: '',
      type: ''
    })

    // Pagination
    const currentPage = ref(1)
    const lastPage = ref(1)
    const perPage = ref(10)
    const total = ref(0)
    const paginationTo = ref(0)
    const paginationFrom = ref(0)
    const searchTimeout = ref(null)

    // Resume des ventes
    const salesCount = ref(0)
    const totalAmount = ref(0)
    const averageSaleAmount = ref(0)

    // Calcul des informations de pagination
    const paginationInfo = computed(() => {
      return {
        from: paginationFrom.value,
        to: paginationTo.value,
        total: total.value
      }
    })

    // Methodes
    const loadSales = async () => {
      try {
        loading.value = true

        const params = {
          page: currentPage.value,
          per_page: perPage.value,
          with: ['customer', 'user']
        }

        if (search.value) {
          params.search = search.value
        }
        if (filters.dateFrom) {
          params.date_from = filters.dateFrom
        }
        if (filters.dateTo) {
          params.date_to = filters.dateTo
        }
        if (filters.type) {
          params.type = filters.type
        }

        const response = await salesService.getSales(params)

        sales.value = response.data?.data || response.data || []
        sales.value.forEach(sale => {
          sale.loading = false
        })

        const meta = response.data?.meta || response.meta || {}
        total.value = meta.total || 0
        perPage.value = meta.per_page || 10
        paginationTo.value = meta.to || 0
        paginationFrom.value = meta.from || 0
        lastPage.value = Math.ceil(total.value / perPage.value)

      } catch (error) {
        console.error('Erreur lors du chargement des ventes:', error)
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      currentPage.value = 1
      clearTimeout(searchTimeout.value)
      searchTimeout.value = setTimeout(() => {
        loadSales()
      }, 500)
    }

    const goToPage = (page) => {
      if (page < 1 || page > lastPage.value) return
      currentPage.value = page
      loadSales()
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''

      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const downloadInvoice = async (sale) => {
      if (sale.loading) return
      sale.loading = true
      try {
        isDownloading.value = true
        await salesService.downloadInvoice(sale.id, true)
        isDownloading.value = false
      } catch (err) {
        isDownloading.value = false
        console.error('Erreur lors du telechargement de la facture:', err)
      } finally {
        sale.loading = false
      }
    }

    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }
    })

    onMounted(() => {
      loadSales()
    })

    return {
      sales,
      loading,
      search,
      filters,
      currentPage,
      lastPage,
      perPage,
      total,
      salesCount,
      totalAmount,
      averageSaleAmount,
      paginationInfo,
      isDownloading,
      downloadInvoice,
      loadSales,
      handleSearch,
      goToPage,
      formatCurrency,
      formatDate
    }
  }
})
</script>
